import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { addDays, format } from 'date-fns';

import { AvailabilityCard, CustomerAvailability } from '@/app/(app)/customers/[id]/availability-card';
import { useCustomerBookingCtx } from '@/app/(app)/customers/[id]/customer-booking-provider';
import { useCustomerCtx } from '@/app/(app)/customers/[id]/provider';
import { DateTime } from '@/components/atoms/datetime';
import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { Skeleton } from '@/components/ui/skeleton';
import { groupByAsList } from '@/lib/group';

export function CustomerBookingResults() {
  const { params } = useCustomerBookingCtx();
  const { data, isLoading, isEnabled } = useAvailabilities();

  if (!isEnabled) {
    return null;
  }
  if (isLoading) {
    return <Skeleton className='h-64 w-full' />;
  }
  if (!data) {
    // TODO: clean up
    return <div>Sorry, something went wrong</div>;
  }
  if (data.availabilities.length === 0) {
    return (
      <div className='flex w-full flex-col items-center justify-center gap-1 rounded-md border border-dashed p-16'>
        <p>No availabilities found</p>
        <p className='text-sm text-muted-foreground'>
          {data.routeCandidates.length > 0
            ? `Checked ${data.routeCandidates.length} routes matching criteria.`
            : 'No routes matching criteria.'}
        </p>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-3'>
      <div className='text-sm font-medium text-muted-foreground'>
        Recommendations for{' '}
        <span className='text-foreground'>
          <DateTime value={params.selectedDate} format='EEEE, MMM d' />
        </span>
      </div>
      <div className='flex flex-col gap-3'>
        {data.availabilities.map((a) => (
          <AvailabilityCard key={a.routeId} availability={a} />
        ))}
      </div>
    </div>
  );
}

function useAvailabilities() {
  const { customer } = useCustomerCtx();
  const { params } = useCustomerBookingCtx();
  const { api } = useApiV1Ctx();

  const isEnabled = !!params.serviceTypeId || !!params.serviceTypeTemplateId;
  const query = useQuery({
    queryKey: [
      'availabilities',
      params.serviceTypeId ?? params.serviceTypeTemplateId ?? '',
      params.selectedDate,
      params.regionIds,
      params.employeeIds,
      params.skillsIds,
      params.window ?? '',
      params.durationMinutes,
      params.preferredDays,
      params.blockedEmployeeIds,
    ],
    enabled: isEnabled,
    queryFn: async () => {
      if (!params.serviceTypeId && !params.serviceTypeTemplateId) return null;
      const res = await api.availability.searchAvailabilitySearchPost({
        body: {
          customer_id: customer.id,
          service_type_id: params.serviceTypeId,
          service_type_template_id: params.serviceTypeTemplateId,
          region_ids: params.regionIds,
          skill_ids: params.skillsIds,
          employee_ids: params.employeeIds,
          exclude_employee_ids: params.blockedEmployeeIds,
          duration_minutes: params.durationMinutes,
          min_advance_minutes: 60,
          time_window: params.window,
          time_of_day: 'ANYTIME',
          from_date: format(params.selectedDate, 'yyyy-MM-dd'),
          to_date: format(addDays(params.selectedDate, 1), 'yyyy-MM-dd'),
        },
      });
      return res.data ?? null;
    },
  });
  const availabilities = useMemo(
    () => mapRankSelectAvailabilities(query.data?.availabilities ?? [], params.selectedDate),
    [query.data, params.selectedDate]
  );
  return {
    data: query.data
      ? {
          availabilities,
          routeCandidates: query.data?.route_candidates ?? [],
        }
      : null,
    isLoading: query.isLoading,
    isEnabled,
  };
}

function mapRankSelectAvailabilities(initial: ApiSchemas['AvailabilityResult']['availabilities'], selectedDate: Date) {
  const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
  // Filter by selected date
  const filtered = initial.filter((a) => a.start_date === selectedDateStr);
  // Group by route
  const byRoute = groupByAsList(filtered, 'route_id');
  // Sort slots for each route
  byRoute.forEach((r) => {
    r.items.sort((a, b) => {
      const distA = a.slots[0].travel_distance_meters;
      const distB = b.slots[0].travel_distance_meters;
      if (distA == null && distB == null) return 0;
      if (distA == null) return 1;
      if (distB == null) return -1;
      return distA - distB;
    });
  });
  // Map to availabilities
  const availabilities: CustomerAvailability[] = byRoute.map((r) => ({
    routeId: r.key,
    employeeId: r.items[0].employee_id ?? null,
    timeWindows: r.items.map((i) => ({
      windowStartTime: i.window_start_time,
      windowEndTime: i.window_end_time,
      bestSlot: i.slots[0],
      slots: i.slots,
    })),
  }));
  // Sort by best slot
  availabilities.sort((a, b) => {
    const distA = a.timeWindows[0].bestSlot.travel_distance_meters;
    const distB = b.timeWindows[0].bestSlot.travel_distance_meters;
    if (distA == null && distB == null) return 0;
    if (distA == null) return 1;
    if (distB == null) return -1;
    return distA - distB;
  });

  return availabilities;
}
